import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function isVisible(el: HTMLElement): boolean {
  if (el.hasAttribute('inert')) return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  if (el.getAttribute('aria-disabled') === 'true') return false;
  // offsetParent === null when display:none (except position:fixed, which we accept).
  return el.offsetParent !== null || getComputedStyle(el).position === 'fixed';
}

function getFocusables(node: HTMLElement): HTMLElement[] {
  return Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isVisible);
}

// Traps Tab/Shift+Tab inside the container while it is mounted. On mount, focuses the
// first focusable element. On unmount, restores focus to the previously-focused element
// only if it is still in the DOM; otherwise falls back to the page main landmark.
export function useFocusTrap<T extends HTMLElement>(active: boolean): RefObject<T | null> {
  const containerRef = useRef<T>(null);
  const previousActive = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    const node = containerRef.current;
    if (!node) return;
    previousActive.current = document.activeElement as HTMLElement | null;
    const initial = getFocusables(node);
    initial[0]?.focus({ preventScroll: true });

    function handleKey(e: KeyboardEvent): void {
      if (e.key !== 'Tab') return;
      const items = getFocusables(node!);
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      // If focus has somehow escaped the container, drag it back rather than letting it
      // wander further.
      if (!node!.contains(document.activeElement)) {
        e.preventDefault();
        first.focus({ preventScroll: true });
        return;
      }
      const current = document.activeElement;
      if (e.shiftKey && current === first) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      const prev = previousActive.current;
      if (prev && document.contains(prev)) {
        prev.focus({ preventScroll: true });
      } else {
        const fallback =
          document.getElementById('main') ?? (document.body as HTMLElement | null);
        fallback?.focus({ preventScroll: true });
      }
    };
  }, [active]);

  return containerRef;
}
